import React, { useCallback, useEffect, useState } from "react";
import { clone, equals, partial, pickBy } from "ramda";

import Box from "@mui/material/Box";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { MenuItem } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import Button from "@mui/material/Button";
import { notBlank } from "../../util/ext";
import TextField from "@mui/material/TextField";

const CadiEventSearchBar = ({ searchState, onSearch }) => {
  const [searchBy, setSearchBy] = useState("DATE");
  const [initialSearchState, _] = useState(clone(searchState));
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [filename, setFilename] = useState(null);
  const [status, setStatus] = useState("COMPLETE");

  useEffect(() => {
    if (notBlank(initialSearchState)) {
      setDateRange({
        startDate: initialSearchState.startDate,
        endDate: initialSearchState.endDate,
      });
    }
  }, [initialSearchState]);

  const handleSearchByChange = useCallback(
    event => {
      setSearchBy(event.target.value);
    },
    [setSearchBy],
  );

  const handleStatusChange = useCallback(
    event => {
      setStatus(event.target.value);
    },
    [setStatus],
  );

  const handleFilenameChange = useCallback(
    event => {
      setFilename(event.target.value);
    },
    [setFilename],
  );

  const handleDateRangeChange = useCallback(
    (key, value) => {
      setDateRange(prevState => ({ ...prevState, [key]: value }));
    },
    [setDateRange],
  );

  const handleSearchClick = useCallback(() => {
    const search = pickBy(notBlank, {
      searchBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      filename: filename,
      status: status,
    });

    console.log("Calling onSearch with:");
    console.log(search);

    onSearch(search);
  }, [
    searchBy,
    dateRange.startDate,
    dateRange.endDate,
    filename,
    status,
    onSearch,
  ]);

  return (
    <Box padding={4}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel id="search-by-select-label">Search By</InputLabel>
            <Select
              labelId="search-by-select-label"
              id="serch-by-select"
              value={searchBy}
              onChange={handleSearchByChange}
            >
              <MenuItem value="DATE">Date</MenuItem>
              <MenuItem value="FILENAME">Filename</MenuItem>
              <MenuItem value="STATUS">Status</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {equals(searchBy, "DATE") && (
          <Grid item xs={6}>
            <Grid container alignItems="center" direction="row" spacing={2}>
              <Grid item xs={6} sm={6}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DatePicker
                    format="MM/dd/yyyy"
                    label="From Date"
                    value={dateRange.startDate}
                    onChange={partial(handleDateRangeChange, ["startDate"])}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        id: "startDate",
                      },
                      openPickerButton: {
                        "aria-label": "change start date",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={6} sm={6}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DatePicker
                    format="MM/dd/yyyy"
                    label="To Date"
                    value={dateRange.endDate}
                    onChange={partial(handleDateRangeChange, ["endDate"])}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        id: "endDate",
                      },
                      openPickerButton: {
                        "aria-label": "change end date",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>
        )}

        {equals(searchBy, "FILENAME") && (
          <Grid item xs={6}>
            <TextField
              id="filename"
              label="Filename"
              fullWidth
              value={filename || ""}
              onChange={handleFilenameChange}
            />
          </Grid>
        )}

        {equals(searchBy, "STATUS") && (
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={status}
                onChange={handleStatusChange}
              >
                <MenuItem value="COMPLETE">Complete</MenuItem>
                <MenuItem value="PROCESSING">Processing</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={3}>
          <Button variant="contained" onClick={handleSearchClick}>
            Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CadiEventSearchBar;
